import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Palette, Layers, Smartphone, Globe, Users, Eye, FileText, Sparkles,
  Type, Film, Accessibility, Activity, Figma, MousePointer, Compass, Gauge,
  PenTool, Search, Wand2, Image as ImageIcon, Brush, Award,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "product-design": {
    eyebrow: "UI/UX · Design",
    title: "Product Design",
    sub: "End-to-end product design — research, IA, UX, UI and a design system that ships.",
    metaDesc: "End-to-end product design services: research, UX, UI, prototyping and design systems for SaaS, mobile and web apps.",
    intro:
      "We design SaaS, mobile and web products from blank canvas to production-ready Figma libraries. Research grounds the work, IA and flows make it usable, visual design makes it desirable, and a tight engineering handoff makes it real.",
    features: [
      { icon: Compass, title: "Research-led", description: "Interviews and analytics shape every flow we ship." },
      { icon: Layers, title: "IA + flows", description: "Sitemaps, user flows, and state diagrams before pixels." },
      { icon: Sparkles, title: "Hi-fi UI", description: "Polished, on-brand screens with micro-interactions." },
      { icon: MousePointer, title: "Prototyping", description: "Clickable Figma prototypes tested on real users." },
      { icon: Palette, title: "Design system", description: "Tokens, components and Figma variables for engineering." },
      { icon: Accessibility, title: "Accessibility", description: "WCAG 2.2 AA from day one, not bolted on later." },
    ],
    process: [
      { step: "01", title: "Discover", description: "Interviews, analytics review, competitive audit." },
      { step: "02", title: "Define", description: "Personas, flows, IA, and success metrics." },
      { step: "03", title: "Design", description: "Hi-fi screens, prototypes, and usability rounds." },
      { step: "04", title: "Deliver", description: "Design system, tokens, and engineering handoff." },
    ],
    deliverables: ["Research synthesis + personas", "User flows + IA", "Hi-fi screens for every flow", "Design system in Figma", "Usability test report", "Engineering handoff package"],
    techStack: ["Figma", "FigJam", "Maze", "Dovetail", "Lottie", "Storybook"],
    pricing: [
      { name: "Sprint", price: "$6k", features: ["1 core flow", "Hi-fi prototype", "5-user test", "2 weeks"] },
      { name: "Full product", price: "$22k", period: "project", highlighted: true, features: ["End-to-end", "Design system", "Usability rounds", "6–9 weeks"] },
      { name: "Design partner", price: "$7k", period: "month", features: ["Dedicated designer", "Weekly reviews", "Continuous iteration"] },
    ],
    faqs: [
      { q: "Will my engineers get tokens?", a: "Yes — Figma variables and JSON tokens ready to wire into Tailwind/Style Dictionary." },
      { q: "Do you handle both web and mobile?", a: "Yes — one team, shared system across web, iOS and Android." },
    ],
  },
  "web-design": {
    eyebrow: "UI/UX · Design",
    title: "Web Design",
    sub: "Marketing and product web design built for clarity, conversion and speed.",
    metaDesc: "Web design services for marketing sites, landing pages and web apps — conversion-focused, accessible, and performance-aware.",
    intro:
      "We design websites that work as hard as your sales team — clear positioning, conversion-focused layouts, performance-aware visuals, and a CMS-friendly content model your marketing team can actually own.",
    features: [
      { icon: Globe, title: "Marketing sites", description: "Positioning, page system, and conversion-first layouts." },
      { icon: Sparkles, title: "Landing pages", description: "Single-purpose pages tuned for paid + organic." },
      { icon: Layers, title: "Web apps", description: "Dashboards, settings, onboarding, billing — done right." },
      { icon: Gauge, title: "Performance-aware", description: "Designs that respect Core Web Vitals from the start." },
      { icon: Type, title: "Typography systems", description: "Modular scale, vertical rhythm, and responsive type." },
      { icon: Accessibility, title: "Accessibility", description: "Contrast, focus, motion-reduce, and screen reader pass." },
    ],
    process: [
      { step: "01", title: "Strategy", description: "Audience, message hierarchy, and conversion goals." },
      { step: "02", title: "System", description: "Type, color, grid, and component primitives." },
      { step: "03", title: "Design", description: "Page-by-page design with motion specs." },
      { step: "04", title: "Handoff", description: "CMS model, tokens, and engineering walkthrough." },
    ],
    deliverables: ["Web design system", "Hi-fi page designs", "CMS content model", "Motion specs", "Asset library", "Engineering handoff"],
    techStack: ["Figma", "Framer", "Lottie", "Rive", "Sanity / Payload"],
    pricing: [
      { name: "Landing page", price: "$3k", features: ["1 conversion page", "Mobile + desktop", "Motion specs", "2 weeks"] },
      { name: "Marketing site", price: "$12k", period: "project", highlighted: true, features: ["8–15 pages", "Design system", "CMS model", "4–6 weeks"] },
      { name: "Web app design", price: "$22k", period: "project", features: ["Dashboards + flows", "Design system", "Usability rounds"] },
    ],
    faqs: [
      { q: "Do you build the site too?", a: "Yes — same team can ship to Next.js, TanStack Start, or hand off to yours." },
      { q: "Can you A/B test?", a: "We design variants and instrument experiments with PostHog or Optimizely." },
    ],
  },
  "mobile-design": {
    eyebrow: "UI/UX · Design",
    title: "Mobile Design",
    sub: "iOS and Android UI/UX that respects native patterns and feels at home.",
    metaDesc: "Mobile UI/UX design for iOS and Android with native pattern fluency, motion and accessibility.",
    intro:
      "Mobile design rewards restraint and pattern fluency. We design iOS apps that respect Apple HIG and Android apps that lean into Material 3 — with motion, haptics and gesture flows that make apps feel inevitable.",
    features: [
      { icon: Smartphone, title: "Native patterns", description: "HIG and Material 3 done right, not generic templates." },
      { icon: Sparkles, title: "Motion + haptics", description: "Subtle motion specs and haptic feedback guidance." },
      { icon: MousePointer, title: "Gesture flows", description: "Swipes, sheets, peeks and gestures tested on device." },
      { icon: Layers, title: "Dynamic type", description: "Layouts that survive accessibility text sizes." },
      { icon: Accessibility, title: "Accessibility", description: "VoiceOver, TalkBack, contrast, and reduced motion." },
      { icon: Palette, title: "Themable UI", description: "Dark mode, system theme sync, and brand variants." },
    ],
    process: [
      { step: "01", title: "Discover", description: "Platform priorities, user flows, native references." },
      { step: "02", title: "Prototype", description: "Figma prototypes installed on real devices." },
      { step: "03", title: "Design", description: "Hi-fi screens with motion + haptic specs." },
      { step: "04", title: "Handoff", description: "Tokens, Lottie/Rive assets, and engineering walkthrough." },
    ],
    deliverables: ["Hi-fi screens for every flow", "Motion + haptic specs", "Dark mode + accessibility states", "Lottie / Rive assets", "Engineering handoff", "Design QA on builds"],
    techStack: ["Figma", "Lottie", "Rive", "Maze", "Principle", "Origami Studio"],
    pricing: [
      { name: "Feature sprint", price: "$5k", features: ["1 flow", "Hi-fi prototype", "Motion specs", "2 weeks"] },
      { name: "Full app design", price: "$20k", period: "project", highlighted: true, features: ["End-to-end", "Design system", "Usability rounds", "6–8 weeks"] },
      { name: "Design retainer", price: "$6k", period: "month", features: ["Dedicated designer", "Design QA", "Continuous iteration"] },
    ],
    faqs: [
      { q: "Do you design for both platforms?", a: "Yes — adapted to each platform's conventions, not a single PWA-style design." },
      { q: "Will you do design QA on builds?", a: "Yes — per-build review with annotated screenshots and Linear tickets." },
    ],
  },
  "design-systems": {
    eyebrow: "UI/UX · Design",
    title: "Design Systems",
    sub: "Token-driven design systems that keep design and engineering in sync.",
    metaDesc: "Design system services: tokens, Figma libraries, component documentation, and Storybook integration.",
    intro:
      "We build design systems your team will actually adopt — Figma variables wired to JSON tokens, components mirrored in Storybook, documentation that explains the why, and governance that keeps drift in check.",
    features: [
      { icon: Palette, title: "Token architecture", description: "Color, type, space, motion tokens across themes." },
      { icon: Layers, title: "Figma libraries", description: "Variants, properties, and composable components." },
      { icon: Figma, title: "Variables + modes", description: "Light/dark, density, and brand modes in Figma." },
      { icon: FileText, title: "Documentation", description: "Usage, do/don't, and accessibility guidance." },
      { icon: Activity, title: "Storybook integration", description: "Design-engineering parity with visual regression." },
      { icon: Compass, title: "Governance", description: "Contribution model, versioning, and adoption tracking." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Inventory existing UI, debt, and naming gaps." },
      { step: "02", title: "Architecture", description: "Token layers, theming model, and library structure." },
      { step: "03", title: "Build", description: "Figma library + Storybook with documentation." },
      { step: "04", title: "Adopt", description: "Migration plan, training, and adoption tracking." },
    ],
    deliverables: ["Token JSON + Figma variables", "Figma component library", "Storybook with docs", "Adoption + governance plan", "Migration playbook", "Team training"],
    techStack: ["Figma", "Tokens Studio", "Style Dictionary", "Storybook", "Chromatic", "Tailwind"],
    pricing: [
      { name: "Audit", price: "$3k", features: ["Inventory + gap report", "Token proposal", "Roadmap", "1 week"] },
      { name: "Build", price: "$22k", period: "project", highlighted: true, features: ["Tokens + library", "Storybook + docs", "Migration plan", "6–8 weeks"] },
      { name: "Stewardship", price: "$4k", period: "month", features: ["New components", "Governance", "Adoption tracking"] },
    ],
    faqs: [
      { q: "Tailwind, CSS-in-JS, or vanilla?", a: "All three. We adapt the token output to your engineering stack." },
      { q: "Will it actually be adopted?", a: "Yes — we plan migrations, run trainings, and track adoption per surface." },
    ],
  },
  "user-research": {
    eyebrow: "UI/UX · Research",
    title: "User Research",
    sub: "Interviews, surveys and field studies that decide what to build next.",
    metaDesc: "User research services: interviews, surveys, diary studies and analytics-driven insight synthesis.",
    intro:
      "We design and run research that produces decisions, not decks. Discovery interviews, jobs-to-be-done framing, diary studies, surveys, and analytics review — synthesized into opportunities your team can act on this quarter.",
    features: [
      { icon: Users, title: "Interviews", description: "Recruited, moderated, and synthesized — clip library included." },
      { icon: FileText, title: "Surveys", description: "Quant studies with proper sampling and segmentation." },
      { icon: Compass, title: "JTBD framing", description: "Jobs-to-be-done synthesis with opportunity scoring." },
      { icon: Search, title: "Diary studies", description: "Multi-day in-context studies for habits and workflows." },
      { icon: Activity, title: "Analytics review", description: "Funnel, retention and behavioral cohort analysis." },
      { icon: Award, title: "Insight synthesis", description: "Themes, opportunities, and prioritized recommendations." },
    ],
    process: [
      { step: "01", title: "Scope", description: "Research questions, success criteria, and method choice." },
      { step: "02", title: "Recruit", description: "Screener design, recruitment, and incentive handling." },
      { step: "03", title: "Run", description: "Moderated/unmoderated sessions with full recording." },
      { step: "04", title: "Synthesize", description: "Themes, opportunities, and decision-ready deck." },
    ],
    deliverables: ["Research plan + screener", "Recordings + transcripts", "Clip library", "Insight report", "Opportunity backlog", "Stakeholder workshop"],
    techStack: ["Dovetail", "User Interviews", "Maze", "Lookback", "Typeform", "Mixpanel", "Notion"],
    pricing: [
      { name: "Quick study", price: "$4k", features: ["6–8 interviews", "Synthesis", "1 readout", "2 weeks"] },
      { name: "Full research", price: "$14k", period: "project", highlighted: true, features: ["Mixed methods", "Recruit + run + synth", "Opportunity backlog", "4–5 weeks"] },
      { name: "Research retainer", price: "$5k", period: "month", features: ["Ongoing recruit", "Monthly readouts", "Continuous insight"] },
    ],
    faqs: [
      { q: "Do you recruit participants?", a: "Yes — we handle recruitment, screeners, scheduling, and incentives end-to-end." },
      { q: "B2B or B2C?", a: "Both — we have networks across SMB, enterprise buyer, and consumer cohorts." },
    ],
  },
  "usability-testing": {
    eyebrow: "UI/UX · Research",
    title: "Usability Testing",
    sub: "Moderated and unmoderated testing that catches issues before launch.",
    metaDesc: "Usability testing services: moderated, unmoderated and benchmark tests with synthesized findings.",
    intro:
      "We test prototypes and live products with the people who actually matter — find the friction, prioritize the fixes, and benchmark progress sprint over sprint.",
    features: [
      { icon: Eye, title: "Moderated tests", description: "5–8 user think-aloud sessions per round." },
      { icon: Activity, title: "Unmoderated tests", description: "Maze, UserTesting, or PlaybookUX at scale." },
      { icon: Gauge, title: "Benchmark studies", description: "SUS, SEQ and task-completion benchmarks over time." },
      { icon: Sparkles, title: "Issue severity scoring", description: "Critical/major/minor with effort estimates." },
      { icon: FileText, title: "Clip library", description: "Video evidence per finding for stakeholders." },
      { icon: Compass, title: "Recommendations", description: "Actionable fixes paired with each issue." },
    ],
    process: [
      { step: "01", title: "Plan", description: "Tasks, recruiting, and success metrics." },
      { step: "02", title: "Recruit", description: "Screen + schedule + incentivize participants." },
      { step: "03", title: "Test", description: "Moderated or unmoderated sessions with recordings." },
      { step: "04", title: "Synthesize", description: "Severity-scored issues with clips and fixes." },
    ],
    deliverables: ["Test plan + script", "Recordings + transcripts", "Severity-scored issue list", "Clip library", "Recommendations doc", "Stakeholder readout"],
    techStack: ["Maze", "UserTesting", "Lookback", "Dovetail", "Figma"],
    pricing: [
      { name: "Single round", price: "$2.5k", features: ["5 users", "1 prototype", "Issue list + clips", "1 week"] },
      { name: "Continuous", price: "$4.5k", period: "month", highlighted: true, features: ["2 rounds/mo", "Recruit included", "Benchmarks"] },
      { name: "Benchmark study", price: "$8k", period: "project", features: ["20–30 users", "SUS + SEQ", "Quant + qual"] },
    ],
    faqs: [
      { q: "How many users per round?", a: "5 is the sweet spot for qualitative — 20+ when you need quant benchmarks." },
      { q: "Prototype or live product?", a: "Both. We test Figma prototypes pre-build and staging/live apps post-launch." },
    ],
  },
  "wireframing": {
    eyebrow: "UI/UX · Research",
    title: "Wireframing",
    sub: "Information architecture and wireframes that align teams before pixels.",
    metaDesc: "Wireframing and information architecture services: sitemaps, user flows, low-fi screens and clickable prototypes.",
    intro:
      "Wireframes are where decisions get cheap. We map IA, draw flows, and sketch low-fi screens so product, design and engineering align on structure before anyone debates a button color.",
    features: [
      { icon: Compass, title: "Information architecture", description: "Sitemaps, navigation models, and content hierarchy." },
      { icon: MousePointer, title: "User flows", description: "End-to-end task flows with edge cases and error states." },
      { icon: Layers, title: "Low-fi wireframes", description: "Greyscale screens that focus on structure, not style." },
      { icon: Sparkles, title: "Clickable prototypes", description: "Linked wireframes for early validation." },
      { icon: FileText, title: "Annotations", description: "Behavior, data, and edge-case notes per screen." },
      { icon: Eye, title: "Stakeholder reviews", description: "Workshops that turn opinions into decisions." },
    ],
    process: [
      { step: "01", title: "Discover", description: "Goals, audience, content audit, and constraints." },
      { step: "02", title: "Structure", description: "Sitemap, navigation, and key flow diagrams." },
      { step: "03", title: "Wireframe", description: "Screen-by-screen low-fi with annotations." },
      { step: "04", title: "Validate", description: "Stakeholder reviews and lightweight user tests." },
    ],
    deliverables: ["Sitemap + IA doc", "User flows + state diagrams", "Annotated wireframes", "Clickable prototype", "Decision log", "Handoff to UI design"],
    techStack: ["Figma", "FigJam", "Whimsical", "Miro"],
    pricing: [
      { name: "Single flow", price: "$2k", features: ["1 user flow", "Wireframes + clickable", "1 review", "1 week"] },
      { name: "Full IA + wires", price: "$8k", period: "project", highlighted: true, features: ["Sitemap + flows", "All screens", "Stakeholder workshops", "3–4 weeks"] },
      { name: "Wireframe retainer", price: "$3k", period: "month", features: ["Ongoing flows", "Weekly reviews", "Async support"] },
    ],
    faqs: [
      { q: "Do we still need wireframes if we have a designer?", a: "Yes — they keep design + engineering + product aligned cheaply before hi-fi work starts." },
      { q: "Will you test wireframes?", a: "Yes — clickable prototypes work well for early task-flow validation." },
    ],
  },
  "brand-identity": {
    eyebrow: "UI/UX · Branding",
    title: "Brand Identity",
    sub: "Full brand identity systems — strategy, voice, visual, and rollout.",
    metaDesc: "Brand identity design services: positioning, voice, visual identity, guidelines and rollout across digital and print.",
    intro:
      "We design brand identity systems with the strategic underpinnings that make them durable — positioning and messaging, visual language, voice, applications, and guidelines your team can extend without asking.",
    features: [
      { icon: Compass, title: "Brand strategy", description: "Positioning, audience, and message hierarchy." },
      { icon: PenTool, title: "Visual identity", description: "Logo, color, typography, illustration, photography." },
      { icon: FileText, title: "Voice + messaging", description: "Tone, vocabulary, and message architecture." },
      { icon: Layers, title: "Applications", description: "Web, product, social, print, swag, environment." },
      { icon: Award, title: "Brand guidelines", description: "Comprehensive guidelines your team will actually follow." },
      { icon: Sparkles, title: "Launch rollout", description: "Migration plan across surfaces with QA support." },
    ],
    process: [
      { step: "01", title: "Strategy", description: "Audience, competition, positioning, and message." },
      { step: "02", title: "Design", description: "Visual language and voice systems together." },
      { step: "03", title: "Apply", description: "Hero applications across every surface that matters." },
      { step: "04", title: "Rollout", description: "Guidelines, training, and migration QA." },
    ],
    deliverables: ["Brand strategy doc", "Logo + visual identity system", "Voice + messaging guide", "Hero applications", "Brand guidelines (digital + print)", "Asset library"],
    techStack: ["Figma", "Adobe Illustrator", "Adobe InDesign", "Notion", "Frontify"],
    pricing: [
      { name: "Visual identity", price: "$8k", features: ["Logo + system", "Core applications", "Mini guidelines", "4 weeks"] },
      { name: "Full brand", price: "$25k", period: "project", highlighted: true, features: ["Strategy + voice", "Visual identity", "Full guidelines", "8–10 weeks"] },
      { name: "Brand partner", price: "$6k", period: "month", features: ["Dedicated designer", "Ongoing applications", "Brand stewardship"] },
    ],
    faqs: [
      { q: "Strategy + design together or separate?", a: "Together — they iterate on each other. We do design-only when strategy already exists." },
      { q: "Do you handle print?", a: "Yes — packaging, signage, print collateral, and production supervision." },
    ],
  },
  "logo-visual": {
    eyebrow: "UI/UX · Branding",
    title: "Logo & Visual",
    sub: "Logo systems, visual identity and asset libraries crafted to last.",
    metaDesc: "Logo design and visual identity services: marks, wordmarks, color, typography and asset libraries.",
    intro:
      "We design logos and visual systems that hold up at every size, every surface, every context. Mark, wordmark, lockups, color, typography — paired with usage guidance so your team uses them right.",
    features: [
      { icon: Brush, title: "Logo system", description: "Mark, wordmark, lockups, monograms, favicons." },
      { icon: Palette, title: "Color palette", description: "Primary, secondary, accent, and accessibility-tested pairs." },
      { icon: Type, title: "Typography", description: "Heading, body, mono with licensing guidance." },
      { icon: ImageIcon, title: "Iconography + illustration", description: "Consistent icon set and illustration style." },
      { icon: Layers, title: "Asset library", description: "All formats: SVG, PNG, PDF, EPS, on/off backgrounds." },
      { icon: FileText, title: "Usage guidelines", description: "Do/don't, clearspace, minimum sizes, misuse examples." },
    ],
    process: [
      { step: "01", title: "Brief", description: "Audience, competitors, references, and constraints." },
      { step: "02", title: "Explore", description: "3–5 distinct directions with rationale." },
      { step: "03", title: "Refine", description: "Iterate the chosen direction across applications." },
      { step: "04", title: "Deliver", description: "Asset library + usage guidelines." },
    ],
    deliverables: ["Logo system (mark + wordmark + lockups)", "Color + typography", "Icon + illustration starter", "Full asset library", "Usage guidelines", "Mock applications"],
    techStack: ["Figma", "Adobe Illustrator", "Glyphs (custom marks)", "Frontify"],
    pricing: [
      { name: "Logo only", price: "$3k", features: ["Mark + wordmark", "Color + type", "Asset pack", "2 weeks"] },
      { name: "Visual identity", price: "$8k", period: "project", highlighted: true, features: ["Logo system", "Color + type + icons", "Guidelines", "4 weeks"] },
      { name: "Identity + rollout", price: "$15k", period: "project", features: ["Full identity", "Hero applications", "Migration support"] },
    ],
    faqs: [
      { q: "How many concepts will you show?", a: "Three distinct directions in round one, each with rationale and applied examples." },
      { q: "Do we own everything?", a: "Yes — full rights, all working files, all formats, all weights." },
    ],
  },
  "motion-graphics": {
    eyebrow: "UI/UX · Branding",
    title: "Motion Graphics",
    sub: "Brand and product motion that adds personality without slowing the app.",
    metaDesc: "Motion graphics services: brand animation, UI micro-interactions, Lottie/Rive animations, and explainer videos.",
    intro:
      "We design motion that earns its keep — brand films, product explainers, UI micro-interactions, Lottie/Rive animations, and motion systems that ship to web, mobile and product at acceptable file sizes.",
    features: [
      { icon: Film, title: "Brand films", description: "Hero films, sizzle reels, and recruiting videos." },
      { icon: Sparkles, title: "Product explainers", description: "30–90s explainer videos for landing pages and ads." },
      { icon: Wand2, title: "UI micro-interactions", description: "Buttons, transitions, and gestures with motion specs." },
      { icon: Activity, title: "Lottie + Rive", description: "Lightweight runtime animations for web and mobile." },
      { icon: Layers, title: "Motion system", description: "Easing, duration, and choreography tokens for engineering." },
      { icon: Award, title: "Sound design", description: "Optional sound design and licensed music." },
    ],
    process: [
      { step: "01", title: "Brief", description: "Goal, audience, length, and references." },
      { step: "02", title: "Storyboard", description: "Frames, voiceover script, and timing draft." },
      { step: "03", title: "Animate", description: "Style frames → animatic → final delivery." },
      { step: "04", title: "Ship", description: "Lottie/Rive export and motion specs for engineering." },
    ],
    deliverables: ["Storyboard + animatic", "Final motion deliverables", "Lottie / Rive files", "Motion system tokens", "Sound + music (optional)", "Source files"],
    techStack: ["After Effects", "Cinema 4D", "Rive", "LottieFiles", "Premiere Pro", "Figma"],
    pricing: [
      { name: "Micro-interactions", price: "$2.5k", features: ["3–5 animations", "Lottie/Rive export", "Motion specs", "1–2 weeks"] },
      { name: "Explainer", price: "$10k", period: "project", highlighted: true, features: ["60–90s video", "Voiceover + music", "Source files", "4 weeks"] },
      { name: "Motion retainer", price: "$5k", period: "month", features: ["Dedicated motion designer", "Ongoing animations", "Motion system stewardship"] },
    ],
    faqs: [
      { q: "Will Lottie play well on mobile?", a: "Yes — we keep files <100KB and test on low-end devices." },
      { q: "Can you match an existing brand?", a: "Yes — we work from your guidelines or align tightly with brand designers." },
    ],
  },
};

export const Route = createFileRoute("/services/ui-ux-design/$child")({
  component: ChildPage,
  loader: ({ params }) => {
    const entry = CHILDREN[params.child];
    if (!entry) throw notFound();
    return { entry };
  },
  head: ({ params }) => {
    const entry = CHILDREN[params.child];
    if (!entry) return {};
    const url = `/services/ui-ux-design/${params.child}`;
    return {
      meta: [
        { title: `${entry.title} — CoderLity` },
        { name: "description", content: entry.metaDesc },
        { property: "og:title", content: `${entry.title} — CoderLity` },
        { property: "og:description", content: entry.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
});

function ChildPage() {
  const { entry } = Route.useLoaderData();
  return <ServicePage data={entry} />;
}
